import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { TextChat } from '@/components/ui/textchat';
import EmojiPicker, { Theme, EmojiClickData } from 'emoji-picker-react';
import { getInitialTheme } from '@/utils/theme';
import { resizeImageClient } from '@/utils/resizeImageClient';
import GolfIcon from '@/components/GolfIcon';
import { useTranslation } from 'react-i18next';
import { IonFooter } from '@ionic/react';

type ChatInputAreaProps = {
  newMessage: string;
  setNewMessage: (message: string) => void;
  onSendMessage: () => void;
  onImageUpload: (file: File) => void;
};

export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  newMessage,
  setNewMessage,
  onSendMessage,
  onImageUpload,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerTheme = getInitialTheme();

  const { t } = useTranslation();

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setNewMessage(newMessage + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (newMessage.trim()) {
        onSendMessage();
      }
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      try {
        const resizedImage = await resizeImageClient(files[0], 800, 300, [1, 1.91]);
        onImageUpload(resizedImage);
      } catch (error) {
        console.error('Error resizing image:', error);
      }
    }
  };

  return (
    <IonFooter className="pb-safe-offset-1">
      <div className="absolute bottom-full left-1/2 z-50 -translate-x-1/2">
        {showEmojiPicker && (
          <div className="mb-2">
            <EmojiPicker
              theme={emojiPickerTheme === 'light' ? Theme.LIGHT : Theme.DARK}
              onEmojiClick={handleEmojiClick}
              searchDisabled
              skinTonesDisabled
              previewConfig={{ showPreview: false }}
              height={350}
              width={300}
            />
          </div>
        )}
      </div>

      <div className="flex items-center mx-1 gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          className="flex-shrink-0"
        >
          <GolfIcon name="image" className="size-6" />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageChange}
            accept="image/*"
            className="hidden"
          />
        </Button>
        <TextChat
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('send_message_placeholder')}
          className="h-10 flex-grow rounded-md bg-secondary border border-input"
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className="flex-shrink-0"
        >
          <GolfIcon name="smile" className="size-6" />
        </Button>
      </div>
    </IonFooter>
  );
};
